import { useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StayPhotoLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photos: { url: string; alt: string | null }[];
  currentIndex: number;
  onSelectIndex: (index: number) => void;
  stayName: string;
}

export const StayPhotoLightbox = ({
  open,
  onOpenChange,
  photos,
  currentIndex,
  onSelectIndex,
  stayName,
}: StayPhotoLightboxProps) => {
  const goNext = useCallback(() => {
    onSelectIndex((currentIndex + 1) % photos.length);
  }, [currentIndex, onSelectIndex, photos.length]);

  const goPrev = useCallback(() => {
    onSelectIndex((currentIndex - 1 + photos.length) % photos.length);
  }, [currentIndex, onSelectIndex, photos.length]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, goNext, goPrev, onOpenChange]);

  if (photos.length === 0) return null;

  const current = photos[currentIndex];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-background/95 border-border/50 backdrop-blur-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="sr-only">{stayName} photos</DialogTitle>

        <div className="relative flex flex-col">

          <div className="relative flex items-center justify-center bg-secondary/30 aspect-[4/3] md:aspect-[16/10]">
            <img
              src={current.url}
              alt={current.alt ?? `${stayName} photo ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            {photos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 rounded-full bg-background/60 hover:bg-background text-foreground"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 rounded-full bg-background/60 hover:bg-background text-foreground"
                  aria-label="Next photo"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
          </div>

          {photos.length > 1 && (
            <div className="p-3 border-t border-border/50">
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                {photos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={() => onSelectIndex(idx)}
                    className={`shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition ${
                      idx === currentIndex ? 'border-primary' : 'border-transparent hover:border-primary/50'
                    }`}
                    aria-label={`View photo ${idx + 1}`}
                    aria-current={idx === currentIndex ? 'true' : undefined}
                  >
                    <img
                      src={photo.url}
                      alt={photo.alt ?? `${stayName} photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
              <div className="text-center text-xs text-muted-foreground mt-2">
                {currentIndex + 1} / {photos.length}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
